import { createSlice } from "@reduxjs/toolkit";
import ApiClientConnector from "src/api/ApiClientConnector";
import { transformToHotelRoomDetails } from "../../actions/hotels/ReservationHotelRoomDetailsActions";

const initialState = {
  propertyDetails: null,
  hotelCost: 0,
  showRoomDetails: false,
  isManageOverview: false,
  isManageClientVoucher: false,
  isNewReservation: false,
  provider: null,
  activeTab: 'hotels',
};

const calculateTotalRoomCost = (room) => {
  const supplementsCost = 
    room.SupplementResults?.reduce(
      (sum, s) => (!s.Included && s.Selected ? sum + s.Value.Total : sum),
      0) || 0;

  const discountTotal =
    room.DiscountResults?.reduce(
      (sum, discount) => (discount.Included ? sum + discount.Value.Total : sum),
      0) || 0;

  const userSelectedDiscount =
    room.DiscountResults?.reduce(
      (sum, discount) => (discount.UserIncluded ? sum + discount.Value.Total : sum),
      0) || 0;

  const netDiscountTotal = userSelectedDiscount - discountTotal;

  const baseCost = room.ClientNetSell + room.ClientNetSellTax;

  return (baseCost + supplementsCost + netDiscountTotal) * (room.RoomCount || 0);
};

export const HotelDetailSlice = createSlice({
  name: "hotelDetails",
  initialState,
  reducers: {
    setHotelRoomDetails: (state, action) => {
      state.propertyDetails = action.payload;
    },
    setHotelRoomDetailsVisibility(state, action) {
      state.showRoomDetails = action.payload; // this "state.showRoomDetails" helps to find single hotel room details
    },
    setReservationOverviewVisibility(state, action) {
      state.isManageOverview = action.payload;
    },
    setReservationClientVoucherVisibility(state, action) {
      state.isManageClientVoucher = action.payload;
    },
    updateRoomCount: (state, action) => {
      const { roomRequestId, roomTypeId, roomCount } = action.payload;

      if (roomCount !== 0 && roomCount !== 1) {
        return;
      }

      const updatedRoomRequestResults = state.propertyDetails?.RoomRequests?.map((roomRequest) => {
        if (roomRequest.roomRequestId !== roomRequestId) return roomRequest;

        const groupedRooms = roomRequest.groupedRooms;
        if (!groupedRooms || typeof groupedRooms !== 'object') return roomRequest;

        const updatedGroupedRooms = {};

        for (const [rateType, rooms] of Object.entries(groupedRooms)) {
          updatedGroupedRooms[rateType] = rooms.map((room) => {
            if (room.RoomTypeId === roomTypeId) {
              return {
                ...room,
                RoomCount: roomCount,
                TotalCost: calculateTotalRoomCost({
                  ...room,
                  RoomCount: roomCount,
                }),
              };
            }
            return {
              ...room, RoomCount: 0, TotalCost: 0
            };
          });
        }

        return {
          ...roomRequest,
          groupedRooms: updatedGroupedRooms,
        };
      });

      const flattenedRooms = updatedRoomRequestResults?.flatMap((roomRequest) =>
        roomRequest.groupedRooms
          ? Object.values(roomRequest.groupedRooms).flat()
          : []
      );

      state.propertyDetails.RoomRequests = updatedRoomRequestResults;
      state.hotelCost = flattenedRooms?.reduce((total, room) => total + calculateTotalRoomCost(room), 0) || 0;
    },

    updateSupplementSelection(state, action) {
      const { roomRequestId, roomTypeId, supplementReferenceId, isSelected } = action.payload;

      const updatedRoomRequests = state.propertyDetails?.RoomRequests?.map((roomRequest) => {
        if (roomRequest.roomRequestId !== roomRequestId) return roomRequest;

        const updatedGroupedRooms = { ...roomRequest.groupedRooms };

        for (const [rateType, rooms] of Object.entries(updatedGroupedRooms)) {
          updatedGroupedRooms[rateType] = rooms.map((room) => {
            if (room.RoomTypeId === roomTypeId) {
              const updatedRoom = { ...room };

              if (updatedRoom.SupplementResults) {
                updatedRoom.SupplementResults = updatedRoom.SupplementResults.map(supplement => {
                  if (supplement.ReferenceId === supplementReferenceId) {
                    return { ...supplement, Selected: isSelected };
                  }
                  return supplement;
                });
              }

              updatedRoom.TotalCost = calculateTotalRoomCost(updatedRoom);
              return updatedRoom;
            }
            return room;
          });
        }

        return {
          ...roomRequest,
          groupedRooms: updatedGroupedRooms
        };
      });

      state.propertyDetails.RoomRequests = updatedRoomRequests;

      const allRooms = updatedRoomRequests.flatMap(request =>
        Object.values(request.groupedRooms).flat()
      );
      state.hotelCost = allRooms.reduce((total, room) => total + (room.TotalCost || 0), 0);
    },

    updateDiscount: (state, action) => {
      const { roomRequestId, roomTypeId, selectedSelectionId } = action.payload;

      const updatedRoomRequests = state.propertyDetails?.RoomRequests?.map((roomRequest) => {
        if (roomRequest.roomRequestId !== roomRequestId) return roomRequest;

        const updatedGroupedRooms = { ...roomRequest.groupedRooms };

        for (const [rateType, rooms] of Object.entries(updatedGroupedRooms)) {
          updatedGroupedRooms[rateType] = rooms.map((room) => {
            if (room.RoomTypeId === roomTypeId) {
              const updatedRoom = { ...room };

              if (updatedRoom.DiscountResults) {
                updatedRoom.DiscountResults = updatedRoom.DiscountResults.map(discount => ({
                  ...discount,
                  UserIncluded: discount.SelectionId === selectedSelectionId
                }));
              }

              updatedRoom.TotalCost = calculateTotalRoomCost(updatedRoom);
              return updatedRoom;
            }
            return room;
          });
        }

        return {
          ...roomRequest,
          groupedRooms: updatedGroupedRooms
        };
      });

      state.propertyDetails.RoomRequests = updatedRoomRequests;

      const allRooms = updatedRoomRequests.flatMap(request =>
        Object.values(request.groupedRooms).flat()
      );
      state.hotelCost = allRooms.reduce((total, room) => total + (room.TotalCost || 0), 0);
    },

    clearHotelRoomDetails: (state) => {
      if (state.propertyDetails?.RoomRequests) {
        state.propertyDetails.RoomRequests = state.propertyDetails.RoomRequests.map(roomRequest => {
          const updatedGroupedRooms = {};

          for (const [rateType, rooms] of Object.entries(roomRequest.groupedRooms || {})) {
            updatedGroupedRooms[rateType] = rooms.map(room => ({
              ...room,
              RoomCount: 0,
              TotalCost: 0
            }));
          }

          return {
            ...roomRequest,
            groupedRooms: updatedGroupedRooms
          };
        });
      }
      state.hotelCost = 0;
      state.showRoomDetails = true;

    },
    clearHotelRoomDetailsFromSearchPage: (state) => {
      if (state.propertyDetails?.RoomRequests) {
        state.propertyDetails.RoomRequests = state.propertyDetails.RoomRequests.map(roomRequest => {
          const updatedGroupedRooms = {};
          for (const [rateType, rooms] of Object.entries(roomRequest.groupedRooms || {})) {
            updatedGroupedRooms[rateType] = rooms.map(room => ({
              ...room,
              RoomCount: 0,
              TotalCost: 0
            }));
          }
          return { ...roomRequest, groupedRooms: updatedGroupedRooms };
        });
      }
      state.propertyDetails = null;
      state.hotelCost = 0;
      state.showRoomDetails = false;
    },
    setNewReservation: (state, action) => {
      state.isNewReservation = action.payload;
    },
    setProvider: (state, action) => {
      state.provider = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
  },
});

export const {
  setHotelRoomDetails,
  setHotelRoomDetailsVisibility,
  setReservationOverviewVisibility,
  setReservationClientVoucherVisibility,
  updateRoomCount,
  updateSupplementSelection,
  updateDiscount,
  clearHotelRoomDetails,
  setNewReservation,
  setProvider,
  setActiveTab,
  clearHotelRoomDetailsFromSearchPage
} = HotelDetailSlice.actions;

export const fetchHotelRoomsDetails =
  (instance, accounts, hotelId, hotelContractId, provider, criteria, hotels, selectedCountry, sessionId) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const response = await client.hotels.postById(hotelId, {
      CheckIn: criteria.CheckIn,
      CheckOut: criteria.CheckOut,
      Honeymoon: criteria.Honeymoon,
      Wedding: criteria.Wedding,
      Rooms: criteria.Rooms,
      Provider: provider,
      HotelContractId: hotelContractId,
      CountryCode: selectedCountry?.code,
      SessionId: sessionId
    });

    const hotelRooms = response.Result || {};
    const hotelRoomDetails = transformToHotelRoomDetails(hotelRooms, hotels);
    dispatch(setHotelRoomDetails(hotelRoomDetails));
  };

export default HotelDetailSlice.reducer;
